/* eslint-disable react/no-unknown-property */
import React, { useMemo, useEffect, useState } from 'react';
import { Html } from '@react-three/drei';
import { twoline2satrec, propagate, gstime, eciToGeodetic, degreesLat, degreesLong } from 'satellite.js';

// 좌표 변환 유틸리티 함수들 (참고 코드와 동일)
const toCartesian = (lat: number, lon: number, height: number) => {
  const latRad = (lat * Math.PI) / 180;
  const lonRad = (lon * Math.PI) / 180;
  const earthRadius = 6371;
  const radius = earthRadius + height;

  const x = radius * Math.cos(latRad) * Math.sin(lonRad);
  const y = radius * Math.sin(latRad);
  const z = radius * Math.cos(latRad) * Math.cos(lonRad);

  return { x, y, z };
};

const scaling = (x: number, y: number, z: number) => {
  const earthRadius = 6371;
  const scaleFactor = 10 / earthRadius;
  return {
    x: x * scaleFactor,
    y: y * scaleFactor,
    z: z * scaleFactor,
  };
};

// 위성 위치 계산 함수 (satellite.js 사용)
const calculateSatellitePosition = (tleFirst: string, tleSecond: string, date: Date) => {
  try {
    // TLE를 satellite.js 객체로 변환
    const satrec = twoline2satrec(tleFirst, tleSecond);
    
    // 특정 시점에서 위성 위치와 속도 계산 (SGP4 알고리즘 사용)
    const positionAndVelocity = propagate(satrec, date);
    const positionEci = positionAndVelocity.position;
    
    if (positionEci && typeof positionEci === 'object') {
      // 현재 시각 기준 그리니치 평균 항성시(GMST) 계산 → 지구 자전각 보정용
      const gmst = gstime(date);
      
      // ECI(우주 기준) 좌표 -> 지구 기준 지리 좌표계(위도, 경도, 고도)로 변환 (GMST 자전 보정 포함)
      const geodetic = eciToGeodetic(positionEci, gmst);
      const latitude = degreesLat(geodetic.latitude);     // 라디안 -> 위도(degrees)
      const longitude = degreesLong(geodetic.longitude);  // 라디안 -> 경도(degrees)
      const altitude = geodetic.height;                             // 고도(km)
      
      const coords = toCartesian(latitude, longitude, altitude);
      const scaled = scaling(coords.x, coords.y, coords.z);
      
      return { 
        x: scaled.x, 
        y: scaled.y, 
        z: scaled.z, 
        latitude, 
        longitude, 
        altitude 
      };
    }
  } catch (error) {
    console.error('위성 위치 계산 오류:', error);
  }
  
  return null;
};

interface SatelliteMarkersProps {
  latestSpacecrafts?: any[];
  onSatelliteClick: (satellite: any) => void;
}

const SatelliteMarkers = ({ latestSpacecrafts, onSatelliteClick }: SatelliteMarkersProps) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // 1초마다 현재 시간 업데이트 (위성 위치 실시간 갱신)
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const satelliteData = useMemo(() => {
    if (!latestSpacecrafts || latestSpacecrafts.length === 0) return [];

    // 각 위성마다 고유한 색상 할당 (궤도선과 일치)
    const colors = [0x103466, 0x1477D2, 0x2046A1];
    
    return latestSpacecrafts.map((spacecraft, index) => {
      if (!spacecraft.tleFirst || !spacecraft.tleSecond) return null;

      try {
        const position = calculateSatellitePosition(spacecraft.tleFirst, spacecraft.tleSecond, currentTime);
        
        if (position) {
          return {
            id: spacecraft.id,
            name: spacecraft.name,
            title: spacecraft.tleTitle || spacecraft.name,
            position: { x: position.x, y: position.y, z: position.z },
            color: colors[index % colors.length],
            spacecraft: spacecraft
          };
        }
      } catch (error) {
        console.error(`위성 ${spacecraft.name} 위치 계산 오류:`, error);
      }
      return null;
    }).filter(Boolean);
  }, [latestSpacecrafts, currentTime]);

  return (
    <>
      {satelliteData.map((satellite, index) => (
        satellite && (
          <group key={index}>
            {/* 위성 메쉬 */}
            <mesh 
              position={[satellite.position.x, satellite.position.y, satellite.position.z]}
              onClick={(event) => {
                event.stopPropagation();
                onSatelliteClick(satellite);
              }}
            >
              <sphereGeometry args={[0.2, 16, 16]} />
              <meshBasicMaterial color={satellite.color} />
            </mesh>
            
            {/* 위성 타이틀 표시 */}
            <Html 
              position={[satellite.position.x, satellite.position.y + 0.8, satellite.position.z]}
              center
              distanceFactor={20}
            >
              <div 
                className="text-white px-3 py-2 rounded text-sm font-medium whitespace-nowrap"
                style={{ 
                  backgroundColor: `#${satellite.color.toString(16).padStart(6, '0')}`,
                  opacity: 0.9
                }}
              >
                {satellite.title}
              </div>
            </Html>
          </group>
        )
      ))}
    </>
  );
};

export default SatelliteMarkers;
